import type { FlightFilter } from '@flightscanner/shared';
import type { RichAircraft } from '../data/enrich';

const EMERGENCY_SQUAWKS = new Set(['7700', '7600', '7500']);

/** Apply a FlightFilter to enriched aircraft. */
export function applyFilter(list: RichAircraft[], f: FlightFilter): RichAircraft[] {
  const q = f.query.trim().toLowerCase();
  const airlineSet = new Set(f.airlines.map((a) => a.toUpperCase()));
  const typeSet = new Set(f.types.map((t) => t.toUpperCase()));
  const classSet = new Set(f.classes);
  const iconSet = new Set(f.iconKinds ?? []);
  const countrySet = new Set(f.countries);

  return list.filter((a) => {
    if (q) {
      const hay = [a.callsign, a.registration, a.icao, a.type, a.airline?.name, a.typeInfo?.model]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }

    if (airlineSet.size && !(a.airline && airlineSet.has(a.airline.icao))) return false;

    if (typeSet.size && !(a.type && typeSet.has(a.type.toUpperCase()))) return false;

    if (classSet.size && !classSet.has(a.flightClass)) return false;

    if (iconSet.size && !iconSet.has(a.iconKind)) return false;

    const alt = a.altBaro ?? 0;
    if (f.altMin != null && alt < f.altMin) return false;
    if (f.altMax != null && alt > f.altMax) return false;

    if (f.speedMin != null && (a.groundSpeed ?? 0) < f.speedMin) return false;
    if (f.speedMax != null && (a.groundSpeed ?? 0) > f.speedMax) return false;

    if (countrySet.size && !(a.inferredCountry && countrySet.has(a.inferredCountry)))
      return false;

    if (f.emergencyOnly) {
      const em = a.squawk && EMERGENCY_SQUAWKS.has(a.squawk);
      const emState = a.emergency && a.emergency !== 'none';
      if (!em && !emState) return false;
    }

    if (f.onGround != null && a.onGround !== f.onGround) return false;

    return true;
  });
}

/** Count aircraft per class for a quick summary badge. */
export function classCounts(list: RichAircraft[]): Record<string, number> {
  const c: Record<string, number> = { commercial: 0, military: 0, private: 0, government: 0 };
  for (const a of list) c[a.flightClass] = (c[a.flightClass] ?? 0) + 1;
  return c;
}
